import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { NavLink } from "@remix-run/react";
import type { ComponentPropsWithoutRef, HTMLAttributes, ReactNode } from "react";
import { HiOutlineChevronDown, HiOutlineChevronRight } from "react-icons/hi2";
import type { NavigationItem } from "~types/app/navigation.types";

import { Wrapper } from "~/components/ui/layout/wrapper";
import { cn } from "~/utils/classnames";

export const SidebarLayout = ({
  children,
  navItems,
  menuTitle,
  navbarClassName,
  ...props
}: {
  children: ReactNode;
  navItems: ComponentPropsWithoutRef<typeof Sidebar>["navItems"];
  menuTitle?: string;
  navbarClassName?: string;
} & ComponentPropsWithoutRef<"div">) => {
  return (
    <div className="lg:flex" {...props}>
      {/* Desktop nav */}
      <Sidebar navItems={navItems} className={cn("hidden shrink-0 lg:block", navbarClassName)} />

      {/* Mobile nav */}
      <Popover className="relative left-0 right-0 top-0 z-10 lg:hidden">
        {({ open, close }) => (
          <>
            <Wrapper className="border-b border-grey-100 py-3">
              <PopoverButton
                as="button"
                className="flex w-full items-center gap-2 font-medium text-grey-800"
              >
                {menuTitle || "Menu"}
                <HiOutlineChevronDown
                  className={cn(open && "-rotate-180", "h-5 w-5 transition-transform")}
                  aria-hidden="true"
                />
              </PopoverButton>
            </Wrapper>
            <Transition
              as="div"
              className="absolute left-0 right-0 top-full overflow-hidden rounded-b-2xl border-b border-grey-100 shadow-md"
              enter="transition ease-in-out duration-250 transform origin-top"
              enterFrom="scale-y-50 opacity-0"
              enterTo="scale-y-100 opacity-100"
              leave="transition ease-in-out duration-250 transform origin-top"
              leaveFrom="scale-y-100 opacity-100"
              leaveTo="scale-y-50 opacity-0"
            >
              <PopoverPanel>
                <Sidebar
                  onClose={close}
                  navItems={navItems}
                  className={cn("w-full", navbarClassName)}
                />
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>

      <div className="grow border-l border-grey-100 pb-16">{children}</div>
    </div>
  );
};

export const Sidebar = ({
  navItems,
  onClose,
  className,
  ...props
}: {
  navItems: NavigationItem[];
  onClose?: () => void;
} & HTMLAttributes<HTMLDivElement>) => {
  // const location = useLocation();

  // const isLinkActive = (link: string) => {
  //   return location.pathname.includes(link);
  // };

  return (
    <nav className={cn("relative h-full w-72 bg-white", className)} {...props}>
      <ul className="space-y-2 px-6 py-8">
        {navItems.map((item) => (
          <li key={item.label}>
            {item.children && item.children.length > 0 ? (
              <Disclosure as="div" defaultOpen>
                {({ open }) => (
                  <>
                    <DisclosureButton
                      className={cn(
                        "flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm font-medium leading-6 text-grey-700 hover:bg-grey-50"
                      )}
                    >
                      {item.icon ? (
                        <item.icon className="h-6 w-6 shrink-0 text-grey-400" aria-hidden="true" />
                      ) : null}
                      {item.label}
                      <HiOutlineChevronRight
                        className={cn(
                          open ? "rotate-90 text-grey-500" : "text-grey-400",
                          "ml-auto h-5 w-5 shrink-0 transition-transform"
                        )}
                        aria-hidden="true"
                      />
                    </DisclosureButton>
                    <Transition
                      enter="transition duration-100 ease-out"
                      enterFrom="transform scale-75 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-75 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-75 opacity-0"
                    >
                      <DisclosurePanel as="ul" className="mt-1 space-y-1 pl-4 pr-0" unmount={false}>
                        {item.children?.map((subItem) => (
                          <li key={subItem.label}>
                            <NavLink
                              to={subItem.to ?? ""}
                              onClick={onClose}
                              className={({ isActive }) =>
                                cn(
                                  isActive ? "bg-grey-50" : "hover:bg-grey-50",
                                  "block rounded-md py-2 pl-7 pr-4 text-sm leading-6 text-grey-700"
                                )
                              }
                            >
                              {subItem.label}
                            </NavLink>
                          </li>
                        ))}
                      </DisclosurePanel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            ) : (
              <NavLink
                to={item.to || "/"}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    isActive ? "bg-grey-50" : "hover:bg-grey-50",
                    "group flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 text-grey-700"
                  )
                }
              >
                {item.icon ? (
                  <item.icon className="h-6 w-6 shrink-0 text-grey-400" aria-hidden="true" />
                ) : null}
                {item.label}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
